import type { WallSideCrossSection } from "building-types/walls/wallSideTypes";
import type { SolidWallMass, SolidWallSide } from "building-types/walls/wallTypes";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { DIST_EPSILON_SQUARED } from "geom/core";
import { polyLine2ReverseCopy } from "geom/polyLine2";
import type { PolyLine2 } from "geom/polyLine2Types";
import { polyLine2New } from "geom/polyLine2Types";
import { vec2DistSquared } from "geom/vec2";
import { getWallAssemblyFinishLayers2 } from "../wallAssemblies/wallAssembly";
import { solidWallCrossSectionEdges } from "./solidWallCrossSectionUtils";

export function wallSideFinishMassCrossSection(
  leftSide: SolidWallSide,
  rightSide: SolidWallSide,
  wallSideFinishMass: SolidWallMass,
  cutPlane?: LevelInches,
) {
  const includeExteriorCasing = true;

  const finishLayerCrossSections: WallSideCrossSection[] = [];

  const { wallAssembly, section, leftEdge, rightEdge, id: finishId, thickness, topFace } = wallSideFinishMass;

  const finishLayers = getWallAssemblyFinishLayers2(wallAssembly, section, false);
  const finishLayer = finishLayers[finishLayers.length - 1];

  if (!finishLayer) {
    return finishLayerCrossSections;
  }

  const innerEdge = leftEdge;
  const outerEdge = polyLine2ReverseCopy(rightEdge);

  const crossSection: PolyLine2[] = [];

  const wallFinishLayerMass = { leftEdge: innerEdge, rightEdge: outerEdge, topFace };
  const { leftOutlines, rightOutlines } = solidWallCrossSectionEdges({
    leftSide,
    rightSide,
    wallMass: wallFinishLayerMass,
    cutPlane,
    includeExteriorCasing,
  });

  for (let i = 0; i < leftOutlines.length; i++) {
    const leftFinishEdge = leftOutlines[i];
    const rightFinishEdge = rightOutlines[i];
    if (
      vec2DistSquared(leftFinishEdge[0], leftFinishEdge[1], leftFinishEdge[2], leftFinishEdge[3]) < DIST_EPSILON_SQUARED
    ) {
      continue;
    }

    crossSection.push(
      polyLine2New([
        ...polyLine2ReverseCopy(rightFinishEdge),
        ...leftFinishEdge,
        rightFinishEdge[2],
        rightFinishEdge[3],
      ]),
    );
  }

  if (crossSection.length) {
    finishLayerCrossSections.push({
      crossSection,
      layer: finishLayer,
      thickness,
      finishId,
    });
  }

  return finishLayerCrossSections;
}
